import { LocalizedText } from "../LocalizedText";
import './Fight.css';
import * as React from 'react';
import Phaser from 'phaser';
import {createPortal} from 'react-dom';
import {isMobileGameDevice} from '../../hooks/useMobileGameViewport';
import {syncBattleInput} from '../../util/battleViewport';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { FightState, endFight, setFight, setFightState, setRewards, setAutoContinue } from '../../slices/fightSlice';
import { useBattlePlayback } from '../../hooks/useBattlePlayback';
import { FightScene, CharacterDataLoader } from './Scenes';
import { setStats } from '../../slices/statSlice';
import { ViewCharacters } from './components/ViewCharacters';
import { FightResult } from './components/FightResult';
import { setBossFight, winFight } from '../../slices/arenaSlice';

import EventEmitter from '../../util/EventEmitter';
import useObjectTranslator from '../../hooks/objectTranslate';
import { Tooltip } from '../Tooltips/Tooltip';
import { Blur } from '../Blur';
import { CenterContainer } from '../CenterContainer/CenterContainer';
import { Spinner } from '../Spinner';
import { cancelFromServer, fromServer, toServer } from '../../util/ServerSocket';
import DefaultScene from '../../features/Fight/Scenes/DefaultScene';
import { CombatPlayerScene } from '../../features/Fight/Scenes/CombatPlayerScene';
import { PreloadScene } from '../../features/Fight/Scenes/PreloadScene';
import Panel from '../Panel/Panel';
import JPanel from '../UI/JPanel';
import { JButton } from '../UI/JButton';
import { useFightActionError } from '../../hooks/useFightActionError';
import { usesIOSSlotPlayback } from '../../util/iosSlotPlayback';
import { IOSSlotPlayback } from './components/IOSSlotPlayback';

// Shared mobile browser / APK battle viewport; desktop keeps its original tree.
function renderFightViewport(content: React.ReactElement, target: HTMLElement, mobile: boolean) {
  return mobile ? createPortal(content, target) : content.props.children;
}
const battleButton: React.CSSProperties = { minWidth: 95, height: 24, border: '1px solid #806033', borderRadius: 4, background: 'linear-gradient(#fff6be, #e5b65a)', color: '#312312', cursor: 'pointer' };

const config = {
  type: Phaser.WEBGL,
  height: 600,
  width: 1000,
  scene: [DefaultScene, CombatPlayerScene, PreloadScene],
  parent: 'fightContainer',
  scale: {
    mode: Phaser.Scale.CENTER_BOTH,
    autoCenter: Phaser.Scale.CENTER_HORIZONTALLY,
  },
  dom: {
    createContainer: true,
  },
  fps: {
    target: 24,
    forceSetTimeOut: true,
  },
};

const Fight: React.FC<{ onClose?: () => void; onFinish?: () => void }> = ({ onClose, onFinish = () => {} }) => {
  const serverCallback = React.useRef<(retry?: any) => void>();
  const [mobile] = React.useState(isMobileGameDevice);
  const gameRef = React.useRef<Phaser.Game>();
  const [retrying, setRetrying] = React.useState(false);
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.fight.state);
  const fight = useAppSelector((state) => state.fight.fight);
  const serverId = useAppSelector((state) => state.account.serverId);
  const autoContinue = useAppSelector(state => state.fight.autoContinue);
  const autoRef = React.useRef(autoContinue); autoRef.current = autoContinue;
  const nextWait = React.useRef<number>();
  const playback = useBattlePlayback(fight);
  React.useEffect(()=>{
    if(!mobile||fight===null)return;
    let frame=0;
    const sync=()=>{cancelAnimationFrame(frame);frame=requestAnimationFrame(()=>syncBattleInput(gameRef.current));};
    window.addEventListener('pknin:battle-layout',sync);window.addEventListener('gameviewportchange',sync);window.addEventListener('resize',sync);sync();
    return()=>{cancelAnimationFrame(frame);window.removeEventListener('pknin:battle-layout',sync);window.removeEventListener('gameviewportchange',sync);window.removeEventListener('resize',sync);};
  },[fight]);
  const vipExpiresAt = useAppSelector(state => state.character.vipExpiresAt ?? 0);
  const rewards = useAppSelector(state => state.fight.rewards);
  const [skipping, setSkipping] = React.useState(false);
  const { error: skipError, show: showActionError, clear: clearActionError } = useFightActionError();
  const [confirmSkipCost, setConfirmSkipCost] = React.useState<number | null>(null);
  const [confirmRebirth, setConfirmRebirth] = React.useState(false);
  const pendingRebirth = React.useRef(false);
  const pendingSkip = React.useRef(false);
  const skipRequest = React.useRef<{ id: string; requestId: string }>();
  const currentFight = React.useRef(fight);
  currentFight.current = fight;
  const cost = vipExpiresAt > Date.now() ? 10 : 50;
  React.useEffect(()=>{
    let timer:number|undefined,done:(()=>void)|undefined;
    const start=EventEmitter.on('provinceWarFight',(data:any,finish:()=>void)=>{
      serverCallback.current=undefined;done=finish;dispatch(setAutoContinue(false));dispatch(setFight(data));
    });
    const finished=EventEmitter.on('combatPlaybackFinished',()=>{
      if(!currentFight.current?.provinceWar||!done)return;
      window.clearTimeout(timer);
      timer=window.setTimeout(()=>{const finish=done;done=undefined;EventEmitter.emit('fightClose');dispatch(endFight());finish?.();},1200);
    });
    const closed=EventEmitter.on('provinceWarFightClosed',()=>{window.clearTimeout(timer);const finish=done;done=undefined;finish?.();});
    return()=>{window.clearTimeout(timer);EventEmitter.off(start);EventEmitter.off(finished);EventEmitter.off(closed);};
  },[dispatch]);
  const iosSlotPlayback = usesIOSSlotPlayback(fight);

  React.useEffect(() => { setConfirmSkipCost(null); setConfirmRebirth(false); }, [fight, rewards]);
  React.useEffect(() => { clearActionError(); }, [fight, clearActionError]);

  function rebirth() {
    if (pendingRebirth.current || !fight?.canRebirth) return;
    pendingRebirth.current = true;
    setConfirmRebirth(false);
    setSkipping(true);
    clearActionError();
    const original = fight;
    const timeout = window.setTimeout(() => { pendingRebirth.current = false; setSkipping(false); if (currentFight.current === original) showActionError('Rebirth', 'ไม่ได้รับคำตอบจากเซิร์ฟเวอร์ กรุณาตรวจสอบรอบต่อสู้ก่อนลองใหม่'); }, 10000);
    toServer('rebirthFight', { id: fight.controlId }, (response: any) => {
      window.clearTimeout(timeout);
      pendingRebirth.current = false;
      setSkipping(false);
      if (currentFight.current !== original) return;
      if (!response?.success) showActionError('Rebirth', response?.error || 'Rebirth ไม่สำเร็จ');
    });
  }

  function skip(confirmedCost?: number) {
    if (pendingSkip.current || rewards !== null) return;
    const original = fight;
    const finish = () => {
      if (currentFight.current !== original) return;
      EventEmitter.emit('fightClose');
      EventEmitter.emit('combatPlaybackFinished');
      _onClose();
      onFinish();
    };
    if (!fight.controlId) { finish(); return; }
    if (confirmedCost === undefined) { setConfirmSkipCost(cost); return; }
    setConfirmSkipCost(null);
    pendingSkip.current = true;
    setSkipping(true);
    clearActionError();
    const timeout = window.setTimeout(() => {
      pendingSkip.current = false;
      setSkipping(false);
      if (currentFight.current === original) showActionError('Skip', 'ไม่ได้รับคำตอบจากเซิร์ฟเวอร์ กรุณาลองอีกครั้ง');
    }, 10000);
    if (skipRequest.current?.id !== fight.controlId) skipRequest.current = { id: fight.controlId, requestId: `${Date.now()}-${Math.random()}` };
    const request = skipRequest.current;
    toServer('skipFight', { ...request, cost: confirmedCost }, (response: any) => {
      window.clearTimeout(timeout);
      if (skipRequest.current !== request) return;
      pendingSkip.current = false;
      setSkipping(false);
      if (currentFight.current !== original) return;
      if (!response?.ok) { showActionError('Skip', response?.error ?? 'Skip ไม่สำเร็จ'); return; }
      skipRequest.current = undefined;
      finish();
    });
  }

  React.useEffect(() => {
    if (!!serverId) {
      toServer('checkFight');
    }
  }, [serverId]);

  React.useEffect(() => {
    fromServer('fight', (data, cb) => {
      dispatch(setFight(data));
      // console.log(data);

      window.clearTimeout(nextWait.current); nextWait.current = undefined;
      if (!data.nextFight || !data.victory) dispatch(setAutoContinue(false));
      setRetrying(false);
      serverCallback.current = (retry: any = false) => {
        if (data.callback && !retry) {
          toServer(data.callback, data.callbackArgs);
        }

        cb?.(retry);
      };
    });
    const ready = (request: { event: string; data: any }) => {
      dispatch(endFight());
      dispatch(setFightState(FightState.LOADING));
      toServer(request.event, request.data);
    };
    fromServer('fightRetryReady', ready);
    fromServer('fightContinueReady', ready);
    const failed = () => { if (autoRef.current) { dispatch(setAutoContinue(false)); if (nextWait.current) dispatch(endFight()); window.clearTimeout(nextWait.current); nextWait.current = undefined; } };
    fromServer('error', failed);

    return () => {
      cancelFromServer('fight');
      cancelFromServer('fightRetryReady', ready);
      cancelFromServer('fightContinueReady', ready);
      cancelFromServer('error', failed);
      window.clearTimeout(nextWait.current);
    };
  }, []);

  React.useEffect(() => {
    if (fight === null || usesIOSSlotPlayback(fight)) return;

    const game = new Phaser.Game({ ...config, ...(mobile ? {scale:{mode:Phaser.Scale.NONE,autoCenter:Phaser.Scale.NO_CENTER}} : {}), callbacks: { preBoot: game => game.registry.set('fightOnFinish', onFinish) } });
    gameRef.current=game;

    return () => {
      gameRef.current=undefined;
      game.destroy(true);
    };
  }, [fight]);

  React.useEffect(() => {
    if (!autoContinue || rewards === null || !fight?.victory || !fight?.nextFight || playback.saving) return;
    const timer = window.setTimeout(() => {
      if (!serverCallback.current) { dispatch(setAutoContinue(false)); return; }
      const finish = serverCallback.current; serverCallback.current = undefined;
      EventEmitter.emit('fightClose'); dispatch(endFight()); dispatch(setFightState(FightState.LOADING));
      nextWait.current = window.setTimeout(() => { dispatch(setAutoContinue(false)); dispatch(endFight()); }, 15000);
      finish({ continue: true });
    }, 1800);
    return () => window.clearTimeout(timer);
  }, [autoContinue, rewards, fight, playback.saving]);

  const _onClose = React.useCallback(() => {
    if(fight?.provinceWar){dispatch(endFight());EventEmitter.emit('provinceWarFightClosed');return;}
    dispatch(setAutoContinue(false));
    (onClose || onCloseDefault)();
    serverCallback.current?.();
    serverCallback.current = undefined;
  }, [fight, onClose]);

  function retry() {
    if (retrying || !serverCallback.current) return;
    setRetrying(true);
    serverCallback.current(true);
    serverCallback.current = undefined;
  }

  const onCloseDefault = React.useCallback(() => {
    dispatch(endFight());

    if (fight.exp) {
      dispatch(setStats({ exp: fight.exp }));
    }

    if (fight.fighterIndex) {
      dispatch(winFight(fight.fighterIndex));
    }
    if (fight.bossFight) {
      dispatch(setBossFight(fight.bossFight));
    }
  }, [fight]);

  if (state === FightState.LOADING) {
    return renderFightViewport(
      <div className="bundled-fight-fullscreen" data-battle-state="loading">
        {!mobile && <Blur />}
        
        <CenterContainer className="bundled-fight-stage" zIndex={20020}>
          <div style={{ width: 1000, height: 600, background: 'black', color: 'whitesmoke' }}>
            <CenterContainer>
              <Spinner width={32} />
            </CenterContainer><LocalizedText text={"Contacting Server..."} /></div>
        </CenterContainer>
      </div>, document.body, mobile
    );
  }

  if (fight === null) return null;

  return renderFightViewport(
    <div className="bundled-fight-fullscreen" data-battle-state="playing">
      {!mobile && <Blur />}
      
      <CenterContainer className="bundled-fight-stage" zIndex={fight?.provinceWar?100020:20020}>
        <div id="fightContainer" style={{ width: 1000, height: 600, position: iosSlotPlayback ? 'relative' : undefined }}>
          {iosSlotPlayback && <IOSSlotPlayback key={fight.controlId} fight={fight} onFinish={() => {
            EventEmitter.emit('combatPlaybackFinished');
            onFinish();
            dispatch(setRewards(fight.rewards ?? {}));
          }} />}
          <div style={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 20, zIndex: 2 }}>
            <button disabled={skipping || playback.saving || !fight.canRebirth} onClick={() => setConfirmRebirth(true)} style={battleButton}>Rebirth</button>
            {autoContinue && <button style={battleButton} onClick={() => dispatch(setAutoContinue(false))}>หยุดอัตโนมัติ</button>}
            <button disabled={skipping || rewards !== null} onClick={() => skip()} style={battleButton}>Skip</button>
          </div>
          {skipError && <div role="alert" style={{ position: 'absolute', bottom: 56, width: '100%', textAlign: 'center', color: '#fff', background: '#800' }}>{skipError}</div>}
          {((confirmSkipCost !== null && rewards === null) || confirmRebirth) && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 20030, background: 'rgba(0,0,0,0.4)' }}>
              <div role="dialog" aria-modal="true" aria-label={confirmRebirth ? "ยืนยัน Rebirth" : "ยืนยันการข้ามฉากต่อสู้"}>
                <Panel name={confirmRebirth ? "ยืนยัน Rebirth" : "ยืนยันการข้ามฉาก"} moveable={false} onClose={() => { setConfirmSkipCost(null); setConfirmRebirth(false); }} style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 20031 }}>
                  <JPanel size={{ width: 380, height: 145 }}>
                    <div style={{ padding: '24px 20px', textAlign: 'center', lineHeight: '24px', color: '#fff' }}>
                      {confirmRebirth ? <>Rebirth มีค่าใช้จ่าย 100 คูปอง<br />เติม HP/MP/VP 100% และเริ่มต่อสู้ใหม่ ยืนยันหรือไม่?</> : <>ข้ามฉากการต่อสู้นี้ มีค่าใช้จ่าย {confirmSkipCost} คูปอง<br />ยืนยันการข้ามฉากหรือไม่?</>}
                    </div>
                    <JButton size={{ width: 100, height: 24 }} position={{ x: 65, y: 110 }} text="ยกเลิก" onClick={() => { setConfirmSkipCost(null); setConfirmRebirth(false); }} />
                    <JButton size={{ width: 100, height: 24 }} position={{ x: 215, y: 110 }} text="ยืนยัน" onClick={() => confirmRebirth ? rebirth() : skip(confirmSkipCost ?? undefined)} />
                  </JPanel>
                </Panel>
              </div>
            </div>
          )}
          <FightResult onClose={_onClose} onReplay={iosSlotPlayback ? undefined : playback.replay} onSave={iosSlotPlayback ? undefined : playback.save} saving={playback.saving} error={playback.error}
            onAutoContinue={() => dispatch(setAutoContinue(!autoContinue))} autoContinue={autoContinue}
            onRetry={serverId && fight.retryRequest && !(fight.players?.[0]?.hp <= 0) ? retry : undefined} retrying={retrying} />
          <ViewCharacters characters={fight.roles} />
          <EffectTooltip />
        </div>
      </CenterContainer>
    </div>, document.body, mobile
  );
};

function EffectTooltip() {
  const t = useObjectTranslator();
  const [id, setId] = React.useState('');
  const [position, setPosition] = React.useState({ x: 0, y: 0 });

  React.useEffect(() => {
    const eventId = EventEmitter.on('effect-icon-enter', ({ id, x, y }: { id: string; x: number; y: number }) => {
      setId(id);
      setPosition({ x, y });
    });

    const leaveEventId = EventEmitter.on('effect-icon-leave', () => {
      setId('');
    });

    return () => {
      EventEmitter.off(eventId);
      EventEmitter.off(leaveEventId);
    };
  }, []);

  if (id === '') {
    return null;
  }

  return <Tooltip text={t(`effect__${id}`)} offsetX={position.x} offsetY={-600 + position.y} />;
}

export default Fight;
